import { HttpErrorResponse } from "@angular/common/http";
import {
  AfterViewChecked,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  computed,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { AnalyticsService } from "@cariloop/analytics";
import { isValidEmail, PasswordComplexity } from "@cariloop/ui-components";
import { TranslateService } from "@ngx-translate/core";
import { LANGUAGES } from "app/languages";
import { AccountRegistrationService } from "app/shared/services/account-registration.service";
import { AccountService } from "app/shared/services/account.service";
import { AppTranslateService } from "app/shared/services/app-translate.service";
import {
  DevicePlatformService,
  MixPanelPlatform,
} from "app/shared/services/device-platform.service";
import { EnrollmentCodeService } from "app/shared/services/enrollment-code.service";
import {
  ProvisioningService,
  ProvisioningProfile,
} from "app/shared/services/provisioning.service";
import type { EmailType } from "app/shared/types/account.type";
import type { CapiError } from "app/shared/types/capi-error.type";
import { LanguageType } from "app/shared/types/language.type";
import { environment } from "environments/environment";
import { Subscription, combineLatest } from "rxjs";
import { startWith, switchMap } from "rxjs/operators";
import { EnrollmentPageLocalizedText } from "../../shared/services/enrollment-code.service";

export type AccountCreationFormSubmittedEventEmitterData = {
  email: string;
  password: string;
  emailType: EmailType;
};

@Component({
  selector: "account-creation",
  templateUrl: "./account-creation.component.html",
  styleUrls: ["./account-creation.component.scss"],
  standalone: false,
})
export class AccountCreationComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  @Input() isMobileWebView = false;
  @Input() voucherCode: string;
  @Input() isSSO: boolean;
  @Input() ssoEmail: string;
  @Input() isHeroB = false;

  @Output()
  formSubmitted =
    new EventEmitter<AccountCreationFormSubmittedEventEmitterData>();
  @ViewChild("emailInput", { static: true, read: ElementRef })
  emailRef: ElementRef;
  @ViewChild("emailTooltip", { static: true, read: ElementRef })
  tooltip: ElementRef;

  readonly errorState = signal<{
    isErrorActive: boolean;
    errorMessage:
      | null
      | "REGISTER.GENERIC_FORM.ERRORS.CONNECTION"
      | "REGISTER.GENERIC_FORM.ERRORS.GENERIC_SERVER_ERROR"
      | "REGISTER.GENERIC_FORM.ERRORS.PASSWORD_COMMON"
      | "REGISTER.GENERIC_FORM.ERRORS.GENERIC";
  }>({ isErrorActive: false, errorMessage: null });
  readonly #enrollmentCodeService = inject(EnrollmentCodeService);

  protected readonly warningState = signal<{
    isWarningActive: boolean;
    warningMessage:
      | null
      | "REGISTER.GENERIC_FORM.ERRORS.ALREADY_REGISTERED"
      | "REGISTER.GENERIC_FORM.ERRORS.MEMBER_ID_MISMATCH";
  }>({
    isWarningActive: false,
    warningMessage: null,
  });

  emailInput: HTMLElement;
  showEmailTypeSelector = true;
  needsMemberId = false;
  emailChanged: boolean = false;
  readonly memberIdPlaceholder = signal<
    EnrollmentPageLocalizedText | undefined
  >(undefined);
  readonly #memberIdLanguage = signal<LanguageType>(LANGUAGES[0]);
  readonly memberIdLabel = computed(() =>
    this.getMemberIdText("REGISTER.GENERIC_FORM.MEMBER_ID"),
  );
  readonly memberIdPlaceholderText = computed(() =>
    this.getMemberIdText("REGISTER.GENERIC_FORM.MEMBER_ID_PLACEHOLDER"),
  );
  buttonText = "";
  focusedElement: HTMLElement;
  hidePassword = true;
  isLoading = false;
  isValidEmail = isValidEmail;
  language: LanguageType = LANGUAGES[0];
  subscriptions: Subscription[] = [];
  passwordComplexity: PasswordComplexity;
  provisioningProfile: ProvisioningProfile;
  platform: MixPanelPlatform;
  webinarId = this.route.snapshot.queryParamMap.get("webinar");
  webinarTitle = this.route.snapshot.queryParamMap.get("webinarTitle");

  creationForm = new FormGroup({
    firstName: new FormControl("", [Validators.required]),
    lastName: new FormControl("", [Validators.required]),
    email: new FormControl("", [
      Validators.required,
      this.createEmailPatternValidator(),
    ]),
    emailType: new FormControl(""),
    memberId: new FormControl(""),
    termsAndPrivacy: new FormControl(false, [Validators.requiredTrue]),
    password: new FormControl("", [Validators.required]),
  });

  constructor(
    private accountService: AccountService,
    private appTranslate: AppTranslateService,
    private translate: TranslateService,
    private accountRegistrationService: AccountRegistrationService,
    private provisioningService: ProvisioningService,
    private analyticsService: AnalyticsService,
    private devicePlatformService: DevicePlatformService,
    private route: ActivatedRoute,
    private titleService: Title,
    private destroyRef: DestroyRef,
  ) {
    this.setLanguage();
  }

  ngOnInit() {
    localStorage.removeItem("cariloop:introductionSeen");
    const introductionSkipped = localStorage.getItem(
      "cariloop:introductionSkipped",
    );
    const createAccountClicked = localStorage.getItem(
      "cariloop:createaccountClicked",
    );
    if (createAccountClicked) {
      localStorage.removeItem("cariloop:createaccountClicked");
      this.analyticsService.trackEvent(
        "register-welcome-carousel-create-account",
      );
    }

    if (introductionSkipped) {
      localStorage.removeItem("cariloop:introductionSkipped");
      this.analyticsService.trackEvent("register-welcome-carousel-skip");
    }
    this.translate
      .stream("REGISTER.PAGE_TITLE")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pageTitle) => {
        this.titleService.setTitle(pageTitle + " | Cariloop");
      });
    this.buttonText = this.translate.instant(
      this.isSSO ? "REGISTER.CONTINUE" : "REGISTER.GENERIC_FORM.CREATE_BTN",
    );
    this.provisioningService
      .getProfileGeneral()
      .pipe(
        switchMap((profile) => {
          this.provisioningProfile = profile;
          this.passwordComplexity = {
            ...profile.passwordComplexity.value,
          };
          return combineLatest([
            this.creationForm.get("firstName").valueChanges.pipe(startWith("")),
            this.creationForm.get("lastName").valueChanges.pipe(startWith("")),
            this.creationForm.get("email").valueChanges.pipe(startWith("")),
          ]);
        }),
      )
      .subscribe(([firstName, lastName, email]) => {
        this.passwordComplexity = {
          ...this.provisioningProfile.passwordComplexity.value,
          containsNameOrEmail: {
            firstName,
            lastName,
            email,
          },
        };
      });

    if (this.emailRef) {
      this.emailInput = this.emailRef.nativeElement;
    }
    this.emailInput
      ?.querySelector("input")
      .addEventListener("blur", () =>
        this.creationForm.controls["email"].updateValueAndValidity(),
      );

    const languageSubscription = this.appTranslate
      .onLangChange()
      .subscribe((lang: string) => this.setNewLang(lang));
    this.subscriptions.push(languageSubscription);

    this.platform = this.devicePlatformService.getPlatformForMixpanel();
    this.analyticsService.trackEvent("create-account", {
      platform: this.platform,
      source: "auth",
      enrollmentCode: this.route.snapshot.queryParamMap.get("enrollmentCode"),
    });
    this.validateCode();
    this.loadPrefillData();
  }

  private loadPrefillData() {
    const prefill = this.accountRegistrationService.registrationPrefill();
    if (prefill) {
      this.creationForm.patchValue({
        firstName: prefill.firstName || "",
        lastName: prefill.lastName || "",
        email: prefill.email || "",
        emailType: prefill.emailType || "",
      });
      const emailControl = this.creationForm.get("email");
      if (emailControl) {
        emailControl.markAsTouched();
        emailControl.updateValueAndValidity();
      }

      this.accountRegistrationService.registrationPrefill.set(null);
    }
  }

  ngAfterViewChecked() {
    if (this.isSSO && this.ssoEmail && !this.emailChanged) {
      this.creationForm.get("email").setValue(this.ssoEmail);
      this.emailChanged = true;
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.emailInput?.querySelector("input").removeAllListeners("blur");
    this.titleService.setTitle("Cariloop");
  }

  onSubmit() {
    const emailType = this.creationForm.get("emailType");
    emailType.addValidators(Validators.required);
    emailType.updateValueAndValidity();
    this.creationForm.markAllAsTouched();
    this.creationForm.updateValueAndValidity();
    const enrollmentCode =
      this.route.snapshot.queryParamMap.get("enrollmentCode");

    if (!this.creationForm.valid) {
      return;
    }
    this.creationForm.disable();
    this.isLoading = true;
    const data = {
      firstName: this.creationForm.get("firstName").value,
      lastName: this.creationForm.get("lastName").value,
      email: this.creationForm.get("email").value,
      emailType: this.creationForm.get("emailType").value,
      ...(this.needsMemberId && {
        memberId: this.creationForm.get("memberId").value.trim(),
      }),
      password: this.creationForm.get("password").value,
      language: this.language.value,
      termsAcceptanceDate: new Date(),
    };
    if (!this.isSSO) {
      this.accountRegistrationService
        .createAccount({
          firstName: this.creationForm.get("firstName").value,
          lastName: this.creationForm.get("lastName").value,
          email: this.creationForm.get("email").value,
          emailType: this.creationForm.get("emailType").value,
          ...(this.needsMemberId && {
            memberId: this.creationForm.get("memberId").value.trim(),
          }),
          password: this.creationForm.get("password").value,
          language: this.language.value,
          termsAcceptanceDate: new Date(),
          voucherCode: this.voucherCode,
          TOSLastModifiedDate: environment.TOS_LAST_MODIFIED_DATE,
          enrollmentCode,
          ...(this.webinarId && { webinarCreationId: this.webinarId }),
        })
        .subscribe({
          next: async (data: any) => {
            const loginRes = await this.accountService
              .login({
                email: this.creationForm.get("email").value,
                password: this.creationForm.get("password").value,
              })
              .toPromise();
            if (loginRes instanceof Error) {
              throw loginRes;
            }

            this.analyticsService.trackEvent("create-account-complete", {
              platform: this.platform,
              source: "auth",
              enrollmentCode: enrollmentCode ? enrollmentCode : null,
              ...(this.webinarId && { webinarId: this.webinarId }),
              ...(this.webinarTitle && {
                webinartitle: decodeURIComponent(this.webinarTitle),
              }),
            });

            localStorage.setItem("login-source", JSON.stringify("register"));

            this.formSubmitted.emit({
              email: this.creationForm.get("email").value,
              password: this.creationForm.get("password").value,
              emailType: this.creationForm.get("emailType").value as EmailType,
            });
          },
          error: (errorResponse) => {
            this.isLoading = false;
            if (errorResponse instanceof HttpErrorResponse) {
              const capiError: CapiError = errorResponse.error;
              const {
                error: { message: errorMessage },
              } = capiError;

              // we only want to show the error on the password not in the gneeral error screen so we will reenable
              // the form and set the error on the password field
              if (
                errorResponse.status === 400 &&
                errorMessage ===
                  "Password is too common or has been reported as compromised"
              ) {
                this.creationForm.enable();

                this.creationForm.get("password").setErrors({
                  knownBadPassword: true,
                });
                return;
              }
              if (
                errorResponse.status === 422 &&
                errorMessage ===
                  "The provided memberId does not match the organization associated with the enrollment code."
              ) {
                this.warningState.set({
                  isWarningActive: true,
                  warningMessage:
                    "REGISTER.GENERIC_FORM.ERRORS.MEMBER_ID_MISMATCH" as const,
                });

                this.creationForm.enable();

                return;
              }
              if (
                errorResponse.status === 422 &&
                errorMessage ===
                  `An account registered with the same email exists, and it isn't in the "eligible" state.`
              ) {
                this.warningState.set({
                  isWarningActive: true,
                  warningMessage:
                    "REGISTER.GENERIC_FORM.ERRORS.ALREADY_REGISTERED" as const,
                });

                this.creationForm.enable();

                return;
              }
            }

            const getErrorMessage = () => {
              if (errorResponse instanceof HttpErrorResponse) {
                if (errorResponse.status === 504) {
                  return "REGISTER.GENERIC_FORM.ERRORS.CONNECTION" as const;
                }

                return "REGISTER.GENERIC_FORM.ERRORS.GENERIC_SERVER_ERROR" as const;
              }

              return "REGISTER.GENERIC_FORM.ERRORS.GENERIC" as const;
            };
            this.errorState.set({
              isErrorActive: true,
              errorMessage: getErrorMessage(),
            });

            this.creationForm.enable();
          },
        });
    } else {
      const logged = this.accountService.getLogged();
      if (logged instanceof Error) {
        this.isLoading = false;
        this.errorState.set({
          isErrorActive: true,
          errorMessage: "REGISTER.GENERIC_FORM.ERRORS.GENERIC",
        });
        this.creationForm.enable();
        return false;
      }
      this.accountService
        .updateAttributes(logged.id, data)
        .pipe(
          switchMap((res) =>
            this.accountRegistrationService.resendVerificationCode(logged.id),
          ),
        )
        .subscribe(
          async () => {
            const loginRes = await this.accountService
              .login({
                email: this.creationForm.get("email").value,
                password: this.creationForm.get("password").value,
              })
              .toPromise();
            if (loginRes instanceof Error) {
              throw loginRes;
            }

            this.analyticsService.trackEvent("create-account-complete", {
              platform: this.platform,
              source: "auth",
            });

            localStorage.setItem("login-source", JSON.stringify("register"));
            this.formSubmitted.emit({
              email: this.creationForm.get("email").value,
              password: this.creationForm.get("password").value,
              emailType: this.creationForm.get("emailType").value as EmailType,
            });
          },
          (error) => {
            this.isLoading = false;
            this.errorState.set({
              isErrorActive: true,
              errorMessage: "REGISTER.GENERIC_FORM.ERRORS.GENERIC",
            });
            this.creationForm.enable();
          },
        );
    }
  }

  validateCode() {
    const enrollmentCode =
      this.route.snapshot.queryParamMap.get("enrollmentCode");
    if (!enrollmentCode) {
      return;
    }
    this.#enrollmentCodeService
      .validateCode(enrollmentCode)
      .subscribe((res) => {
        this.needsMemberId = res?.needsMemberId === true;
        this.memberIdPlaceholder.set(res?.memberIdPlaceholder);
        const memberIdControl = this.creationForm.controls.memberId;
        if (this.needsMemberId) {
          memberIdControl.setValidators([
            Validators.required,
            Validators.pattern(/\S/),
          ]);
        } else {
          memberIdControl.clearValidators();
        }
        memberIdControl.updateValueAndValidity();

        if (res?.name === "Freemium") {
          this.showEmailTypeSelector = false;
          this.creationForm.controls["emailType"].setValue("personal");
        } else {
          this.showEmailTypeSelector = true;
        }
      });
  }

  changeLang(lang: string) {
    const newLanguage = this.setNewLang(lang);
    this.appTranslate.updateLang(lang);
    return newLanguage;
  }

  setLanguage() {
    const localLanguage = this.accountService.getLocalLanguage();
    if (localLanguage) {
      this.language =
        LANGUAGES.find((lang) => lang.value === localLanguage?.value) ??
        LANGUAGES[0];
      this.#memberIdLanguage.set(this.language);
    } else {
      this.language =
        LANGUAGES.find(
          (lang) => lang.value === window.navigator.language.substring(0, 2),
        ) ?? LANGUAGES[0];
      this.#memberIdLanguage.set(this.language);
      this.appTranslate.updateLang(this.language.value);
    }
  }

  setNewLang(lang: string) {
    const newLanguage = LANGUAGES.find((l) => l.value === lang);
    if (newLanguage) {
      this.accountService.setLocalLanguage(newLanguage);
      this.language = newLanguage;
      this.#memberIdLanguage.set(newLanguage);
    }
    return newLanguage;
  }

  getMemberIdText(defaultTranslationKey: string): string {
    const memberIdPlaceholder = this.memberIdPlaceholder();
    const language =
      this.#memberIdLanguage().value as keyof EnrollmentPageLocalizedText;

    return (
      memberIdPlaceholder?.[language] ??
      memberIdPlaceholder?.en ??
      this.translate.instant(defaultTranslationKey)
    );
  }

  private createEmailPatternValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) {
        return null;
      }

      if (this.emailInput?.querySelector("input") === document.activeElement) {
        return null;
      }

      return !isValidEmail(value) ? { pattern: true } : null;
    };
  }
}
